import { useEffect, useMemo, useState } from 'react';
import { Card, ConfirmModal, Field, Modal } from '../../components/UI';
import { useToast } from '../../components/Toast';
import { fetchCrDrPage, listenAccounts, savePlan, saveTransaction } from '../../firebase/repo';
import { normalizeDateISO, parseBulkLines, todayISO } from '../../utils/date';
import { buildTransactionTemplateXlsx, parseTransactionXlsx, type XlsxRow } from '../../utils/xlsxTemplate';
import { DROPDOWN_GROUPS, defaultDropdownValue, dropdownOptions } from '../../types/dropdowns';
import type { Account, Contact, CrDrEntry, Flow, MasterType, PlanFrequency, Transaction } from '../../types';
import { CrDrPanel } from '../crdr/CrDrPanel';
import { AccountTools } from '../accounts/AccountTools';

type PendingAction = 'daily' | 'bulk' | 'plan' | null;
type AddTab = 'daily' | 'bulk' | 'crdr' | 'plan' | 'transfer' | 'card';
type CsvImportRow = {
  selected: boolean;
  errors: string[];
  warnings: string[];
  accountName?: string;
  transaction: Partial<Transaction>;
};

const splitCsvLine = (line: string) => {
  const cells: string[] = [];
  let value = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"' && line[i + 1] === '"') {
      value += '"';
      i += 1;
    } else if (char === '"') {
      quoted = !quoted;
    } else if (char === ',' && !quoted) {
      cells.push(value.trim());
      value = '';
    } else {
      value += char;
    }
  }
  cells.push(value.trim());
  return cells;
};

const normalizeLookup = (value?: string) => String(value || '').trim().toLowerCase();
const toISOFromImportDate = (value: string) => {
  const raw = String(value || '').trim();
  const serial = Number(raw);
  if (/^\d+(\.\d+)?$/.test(raw) && serial > 20000) {
    const date = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
    return date.toISOString().slice(0, 10);
  }
  return normalizeDateISO(raw);
};

export function TransactionForm({ uid, grouped, contacts }: { uid: string; grouped: Record<MasterType, string[]>; contacts: Contact[] }) {
  const toast = useToast();
  const [tab, setTab] = useState<AddTab>('daily');
  const [tx, setTx] = useState<Partial<Transaction>>({ date: todayISO(), type: 'Expense', paymentMode: defaultDropdownValue(DROPDOWN_GROUPS.PAYMENT_MODE) });
  const [file, setFile] = useState<File | undefined>();
  const [bulk, setBulk] = useState('');
  const [csvRows, setCsvRows] = useState<CsvImportRow[]>([]);
  const [csvSaving, setCsvSaving] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [existingCrdr, setExistingCrdr] = useState<CrDrEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [plan, setPlan] = useState({ title: '', category: defaultDropdownValue(DROPDOWN_GROUPS.PLAN_CATEGORY), flow: 'Expense' as Flow, paymentMode: defaultDropdownValue(DROPDOWN_GROUPS.PAYMENT_MODE), paidFromAccountId: '', paidFromAccountName: '', paidFromAccountType: undefined as Account['type'] | undefined, amount: 0, dueDate: todayISO(), frequency: defaultDropdownValue(DROPDOWN_GROUPS.PLAN_FREQUENCY) as PlanFrequency, totalCount: 12, contactName: '', note: '', calendarSyncEnabled: false, reminderDaysBefore: 3 });
  const cats = tx.type === 'Income'
    ? dropdownOptions(grouped, DROPDOWN_GROUPS.INCOME_CATEGORY)
    : tx.type === 'Investment'
      ? dropdownOptions(grouped, DROPDOWN_GROUPS.INVESTMENT_CATEGORY)
      : dropdownOptions(grouped, DROPDOWN_GROUPS.DAILY_CATEGORY);
  const paymentModes = dropdownOptions(grouped, DROPDOWN_GROUPS.PAYMENT_MODE);
  const flows = dropdownOptions(grouped, DROPDOWN_GROUPS.FLOW);
  const allCategories = useMemo(() => Array.from(new Set([
    ...dropdownOptions(grouped, DROPDOWN_GROUPS.DAILY_CATEGORY),
    ...dropdownOptions(grouped, DROPDOWN_GROUPS.INCOME_CATEGORY),
    ...dropdownOptions(grouped, DROPDOWN_GROUPS.INVESTMENT_CATEGORY)
  ].filter(Boolean))), [grouped]);
  const planCategories = dropdownOptions(grouped, DROPDOWN_GROUPS.PLAN_CATEGORY);
  const planFrequencies = dropdownOptions(grouped, DROPDOWN_GROUPS.PLAN_FREQUENCY);
  const taxTags = dropdownOptions(grouped, DROPDOWN_GROUPS.TAX_TAG);
  const bulkPlaceholder = `${todayISO()}, ${cats[0] || defaultDropdownValue(DROPDOWN_GROUPS.DAILY_CATEGORY)}, 120, ${paymentModes[0] || defaultDropdownValue(DROPDOWN_GROUPS.PAYMENT_MODE)}, note`;
  useEffect(() => listenAccounts(uid, setAccounts), [uid]);
  useEffect(() => { fetchCrDrPage(uid, '', 200).then(setExistingCrdr).catch(() => setExistingCrdr([])); }, [uid]);
  const selectableAccounts = useMemo(() => accounts.filter((account) => account.status === 'active' && account.active !== false).filter((account) => account.type !== 'credit_card' || tx.type === 'Expense'), [accounts, tx.type]);
  const planPayAccounts = useMemo(() => accounts.filter((account) => account.status === 'active' && account.active !== false && account.type !== 'credit_card'), [accounts]);
  const selectedAccount = accounts.find((account) => account.id === tx.accountId);
  const activeAccountByName = useMemo(() => new Map(accounts.filter((account) => account.status === 'active' && account.active !== false).map((account) => [normalizeLookup(account.name), account])), [accounts]);
  const validCategories = useMemo(() => new Set(allCategories.map(normalizeLookup)), [allCategories]);
  const validPaymentModes = useMemo(() => new Set(paymentModes.map(normalizeLookup)), [paymentModes]);

  const chooseAccount = (accountId: string) => {
    const account = accounts.find((item) => item.id === accountId);
    setTx({ ...tx, accountId, accountName: account?.name || '', accountType: account?.type });
  };
  const choosePlanAccount = (accountId: string) => {
    const account = accounts.find((item) => item.id === accountId);
    setPlan({ ...plan, paidFromAccountId: accountId, paidFromAccountName: account?.name || '', paidFromAccountType: account?.type });
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const type = tx.type || 'Expense';
      const paymentMode = tx.paymentMode || paymentModes[0] || defaultDropdownValue(DROPDOWN_GROUPS.PAYMENT_MODE);
      await saveTransaction(uid, { ...tx, type, category: tx.category || cats[0] || defaultDropdownValue(DROPDOWN_GROUPS.DAILY_CATEGORY), paymentMode, amount: Number(tx.amount || 0) }, file);
      setTx({ date: todayISO(), type, category: cats[0] || defaultDropdownValue(DROPDOWN_GROUPS.DAILY_CATEGORY), paymentMode });
      setFile(undefined);
      toast('Transaction saved');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Transaction save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveBulk = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const rows = parseBulkLines(bulk, tx.type || 'Expense', tx.paymentMode || paymentModes[0] || defaultDropdownValue(DROPDOWN_GROUPS.PAYMENT_MODE));
      await Promise.all(rows.map((r) => saveTransaction(uid, r)));
      setBulk('');
      toast(`${rows.length} entries saved`);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Bulk save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const downloadTransactionTemplate = () => {
    const csv = 'date,type,category,amount,paymentMode,accountName,taxTag,note,crdrPerson\n' +
      `${todayISO()},Expense,Food,120,UPI,,Personal Expense,Lunch,\n`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'pfs-transaction-import-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcelTemplate = () => {
    const activeAccounts = accounts.filter((account) => account.status === 'active' && account.active !== false).map((account) => account.name);
    const contactNames = contacts.map((contact) => contact.name || contact.phone || contact.email || '').filter(Boolean);
    const blob = buildTransactionTemplateXlsx({
      types: ['Expense', 'Income', 'Investment'],
      categories: allCategories.length ? allCategories : cats,
      paymentModes,
      accounts: activeAccounts,
      contacts: contactNames,
      taxTags,
      today: todayISO()
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pfs-bulk-transaction-template-${todayISO()}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const buildImportRows = (objects: XlsxRow[]) => objects.map((row) => {
    const normalized = new Map(Object.entries(row).map(([key, value]) => [normalizeLookup(key).replace(/[^a-z0-9]/g, ''), String(value || '').trim()]));
    const get = (...names: string[]) => names.map((name) => normalized.get(normalizeLookup(name).replace(/[^a-z0-9]/g, '')) || '').find(Boolean) || '';
    const type = get('type') as Flow;
    const amount = Number(get('amount'));
    const accountName = get('account', 'accountName');
    const account = activeAccountByName.get(normalizeLookup(accountName));
    const category = get('category');
    const paymentMode = get('paymentMode', 'payment mode');
    const taxTag = get('taxTag', 'tax tag');
    const errors: string[] = [];
    const warnings: string[] = [];
    if (!get('date')) errors.push('Date missing');
    if (!['Expense', 'Income', 'Investment'].includes(type)) errors.push('Type must be Expense, Income, or Investment');
    if (!category) errors.push('Category missing');
    if (category && !validCategories.has(normalizeLookup(category))) warnings.push('Category is not in current masters');
    if (!Number.isFinite(amount) || amount <= 0) errors.push('Amount must be greater than 0');
    if (!paymentMode) errors.push('Payment mode missing');
    if (paymentMode && !validPaymentModes.has(normalizeLookup(paymentMode))) warnings.push('Payment mode is not in current masters');
    if (accountName && !account) warnings.push(`Account not found, will import as Unassigned: ${accountName}`);
    if (account?.type === 'credit_card' && type !== 'Expense') errors.push('Credit card account can only be used for Expense rows');
    const note = get('note');
    return {
      selected: errors.length === 0,
      errors,
      warnings,
      accountName,
      transaction: {
        date: toISOFromImportDate(get('date')),
        type,
        category,
        amount,
        paymentMode,
        taxTag,
        isTaxRelevant: Boolean(taxTag && taxTag !== 'Ignore for ITR'),
        accountId: account?.id || '',
        accountName: account?.name || '',
        accountType: account?.type,
        note,
        crdrPerson: get('crdrPerson', 'cr/dr person', 'crdr person'),
        source: 'BulkEntry'
      }
    } satisfies CsvImportRow;
  }).filter((row) => row.transaction.date || row.transaction.category || row.transaction.amount || row.transaction.note);

  const parseTransactionUpload = async (file?: File) => {
    if (!file) return;
    try {
      const rows = file.name.toLowerCase().endsWith('.xlsx')
        ? buildImportRows(await parseTransactionXlsx(file))
        : buildImportRows(await parseCsvFile(file));
      if (!rows.length) throw new Error('Uploaded file has no transaction rows.');
      setCsvRows(rows);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Import file failed', 'error');
    }
  };

  const parseCsvFile = async (file: File): Promise<XlsxRow[]> => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length < 2) throw new Error('CSV file has no transaction rows.');
    const headers = splitCsvLine(lines[0]);
    return lines.slice(1).map((line) => {
      const cells = splitCsvLine(line);
      return Object.fromEntries(headers.map((header, index) => [header, cells[index] || '']));
    });
  };

  const saveCsvRows = async () => {
    if (csvSaving) return;
    const selected = csvRows.filter((row) => row.selected);
    const invalidSelected = selected.filter((row) => row.errors.length);
    if (!selected.length) return toast('Select at least one valid row', 'error');
    if (invalidSelected.length) return toast('Fix invalid rows before importing', 'error');
    setCsvSaving(true);
    try {
      for (let index = 0; index < selected.length; index += 1) {
        await saveTransaction(uid, selected[index].transaction);
      }
      setCsvRows([]);
      toast(`${selected.length} transaction(s) imported`);
    } catch (error) {
      toast(error instanceof Error ? `Import stopped: ${error.message}` : 'Import stopped after a Firestore error', 'error');
    } finally {
      setCsvSaving(false);
    }
  };

  const savePlanClick = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await savePlan(uid, plan);
      setPlan({ ...plan, title: '', amount: 0, note: '' });
      toast('Plan saved');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Plan save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirm = async () => {
    if (saving) return;
    if (pendingAction === 'daily') await save();
    if (pendingAction === 'bulk') await saveBulk();
    if (pendingAction === 'plan') await savePlanClick();
    setPendingAction(null);
  };

  return <div className="page add-page">
    <div className="seg page-seg"><button className={tab === 'daily' ? 'active' : ''} onClick={() => setTab('daily')}>Daily Entry</button><button className={tab === 'bulk' ? 'active' : ''} onClick={() => setTab('bulk')}>Bulk Entry</button><button className={tab === 'crdr' ? 'active' : ''} onClick={() => setTab('crdr')}>CR/DR Entry</button><button className={tab === 'plan' ? 'active' : ''} onClick={() => setTab('plan')}>Plan Setup</button><button className={tab === 'transfer' ? 'active' : ''} onClick={() => setTab('transfer')}>Transfer</button><button className={tab === 'card' ? 'active' : ''} onClick={() => setTab('card')}>Pay Card</button></div>
    {tab === 'daily' ? <>
      <Card className="form-card">
        <div className="section-title"><div><p className="eyebrow">Quick add</p><h2>Add daily transaction</h2><p className="muted">Save income, expense or investment with receipt.</p></div></div>
        <div className="grid2">
          <Field label="Date"><input type="date" value={tx.date} onChange={(e) => setTx({ ...tx, date: e.target.value })} /></Field>
          <Field label="Type"><select value={tx.type} onChange={(e) => setTx({ ...tx, type: e.target.value as Flow, category: '', ...(e.target.value !== 'Expense' && selectedAccount?.type === 'credit_card' ? { accountId: '', accountName: '', accountType: undefined } : {}) })}>{flows.map((x) => <option key={x}>{x}</option>)}</select></Field>
        </div>
        <div className="grid2">
          <Field label="Category"><select value={tx.category || cats[0]} onChange={(e) => setTx({ ...tx, category: e.target.value })}>{cats.map((x) => <option key={x}>{x}</option>)}</select></Field>
          <Field label="Amount"><input inputMode="decimal" type="number" value={tx.amount || ''} onChange={(e) => setTx({ ...tx, amount: Number(e.target.value) })} placeholder="0" /></Field>
        </div>
        <div className="grid2">
          <Field label="Payment mode"><select value={tx.paymentMode || paymentModes[0]} onChange={(e) => setTx({ ...tx, paymentMode: e.target.value })}>{paymentModes.map((x) => <option key={x}>{x}</option>)}</select></Field>
          <Field label="Paid from / Account"><select value={tx.accountId || ''} onChange={(e) => chooseAccount(e.target.value)}><option value="">Unassigned</option>{selectableAccounts.map((account) => <option key={account.id} value={account.id}>{account.name} ({account.type.replace('_', ' ')})</option>)}</select></Field>
        </div>
        {selectedAccount && <p className="muted">Account impact: {selectedAccount.type === 'credit_card' ? 'credit card outstanding will increase.' : tx.type === 'Income' ? 'balance will increase.' : 'balance will reduce.'}</p>}
        <div className="grid2">
          <Field label="CR/DR person"><input list="contacts" value={tx.crdrPerson || ''} onChange={(e) => setTx({ ...tx, crdrPerson: e.target.value })} placeholder="Optional" /></Field>
          <Field label="Receipt"><input type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0])} /></Field>
        </div>
        <details className="advanced-box"><summary>More details</summary><div className="grid2"><Field label="Tax Tag"><select value={tx.taxTag || ''} onChange={(e) => setTx({ ...tx, taxTag: e.target.value, isTaxRelevant: Boolean(e.target.value && e.target.value !== 'Ignore for ITR') })}><option value="">No tax tag</option>{taxTags.map((tag) => <option key={tag}>{tag}</option>)}</select></Field><Field label="Report group"><input value={tx.reportGroup || ''} onChange={(e) => setTx({ ...tx, reportGroup: e.target.value })} placeholder="Optional" /></Field></div></details>
        <Field label="Note"><input value={tx.note || ''} onChange={(e) => setTx({ ...tx, note: e.target.value })} placeholder="Optional note" /></Field>
        <button className="btn primary wide action-save" disabled={saving || !Number(tx.amount || 0)} onClick={() => setPendingAction('daily')}>{saving ? 'Saving...' : 'Save transaction'}</button>
      </Card>
    </> : tab === 'bulk' ? <>
      <Card>
        <div className="section-title"><div><h3>Bulk entry</h3><p className="muted">One line per transaction.</p></div></div>
        <textarea value={bulk} onChange={(e) => setBulk(e.target.value)} placeholder={bulkPlaceholder} />
        <button className="btn wide" disabled={saving || !bulk.trim()} onClick={() => setPendingAction('bulk')}>{saving ? 'Saving...' : 'Save bulk'}</button>
        <div className="import-tools">
          <p className="muted">Download a fresh template when you add new categories, payment modes, accounts, or contacts.</p>
          <div className="row-actions"><button className="btn small primary" onClick={downloadExcelTemplate}>Download Excel template</button><button className="btn small" onClick={downloadTransactionTemplate}>Download CSV template</button><label className="btn small">Upload file<input type="file" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={(e) => parseTransactionUpload(e.target.files?.[0])} /></label></div>
        </div>
      </Card>
    </> : tab === 'crdr' ? <CrDrPanel uid={uid} grouped={grouped} contacts={contacts} existingCrdr={existingCrdr} /> : tab === 'transfer' || tab === 'card' ? <Card><AccountTools uid={uid} accounts={accounts} compact /></Card> : <Card className="form-card">
      <div className="section-title"><div><p className="eyebrow">Plan setup</p><h2>Add EMI/SIP plan</h2><p className="muted">Create a repeating or future payment.</p></div></div>
      <Field label="Title"><input value={plan.title} onChange={(e) => setPlan({ ...plan, title: e.target.value })} placeholder="Plan title" /></Field>
      <div className="grid2">
        <Field label="Category"><select value={plan.category} onChange={(e) => setPlan({ ...plan, category: e.target.value })}>{planCategories.map((x) => <option key={x}>{x}</option>)}</select></Field>
        <Field label="Flow"><select value={plan.flow} onChange={(e) => setPlan({ ...plan, flow: e.target.value as Flow })}>{flows.map((x) => <option key={x}>{x}</option>)}</select></Field>
      </div>
      <Field label="Payment mode"><select value={plan.paymentMode} onChange={(e) => setPlan({ ...plan, paymentMode: e.target.value })}>{paymentModes.map((x) => <option key={x}>{x}</option>)}</select></Field>
      <Field label="Paid from / Account"><select value={plan.paidFromAccountId} onChange={(e) => choosePlanAccount(e.target.value)}><option value="">Unassigned</option>{planPayAccounts.map((account) => <option key={account.id} value={account.id}>{account.name} ({account.type.replace('_', ' ')})</option>)}</select></Field>
      <div className="grid2">
        <Field label="Amount"><input inputMode="decimal" type="number" value={plan.amount || ''} onChange={(e) => setPlan({ ...plan, amount: Number(e.target.value) })} placeholder="0" /></Field>
        <Field label="Due date"><input type="date" value={plan.dueDate} onChange={(e) => setPlan({ ...plan, dueDate: e.target.value })} /></Field>
      </div>
      <div className="grid2">
        <Field label="Frequency"><select value={plan.frequency} onChange={(e) => setPlan({ ...plan, frequency: e.target.value as PlanFrequency })}>{planFrequencies.map((x) => <option key={x}>{x}</option>)}</select></Field>
        <Field label="Total count"><input inputMode="numeric" type="number" value={plan.totalCount} onChange={(e) => setPlan({ ...plan, totalCount: Number(e.target.value) })} /></Field>
      </div>
      <div className="calendar-box">
        <label className="toggle-line"><input type="checkbox" checked={plan.calendarSyncEnabled} onChange={(e) => setPlan({ ...plan, calendarSyncEnabled: e.target.checked })} /> <span>Sync calendar reminder</span></label>
        <Field label="Reminder before due"><select value={plan.reminderDaysBefore} onChange={(e) => setPlan({ ...plan, reminderDaysBefore: Number(e.target.value) })}><option value={0}>Same day</option><option value={1}>1 day before</option><option value={3}>3 days before</option><option value={7}>7 days before</option></select></Field>
        <p className="muted">Google Calendar sync is prepared here. Full sync requires Calendar API, OAuth consent, and calendar.events permission.</p>
      </div>
      <Field label="Person"><input list="contacts" value={plan.contactName} onChange={(e) => setPlan({ ...plan, contactName: e.target.value })} placeholder="Optional" /></Field>
      <Field label="Note"><input value={plan.note} onChange={(e) => setPlan({ ...plan, note: e.target.value })} placeholder="Optional note" /></Field>
      <button className="btn primary wide action-save" disabled={saving || !plan.title || !Number(plan.amount || 0)} onClick={() => setPendingAction('plan')}>{saving ? 'Saving...' : 'Save plan'}</button>
    </Card>}
    <datalist id="contacts">{contacts.map((c) => <option key={c.id} value={c.name} />)}</datalist>
    <Modal open={!!csvRows.length} title="Transaction import preview" onClose={() => setCsvRows([])}>
      <div className="csv-summary"><b>{csvRows.filter((row) => row.selected).length}</b><span>selected</span><b>{csvRows.filter((row) => row.errors.length).length}</b><span>invalid</span><b>{csvRows.filter((row) => row.warnings.length).length}</b><span>warnings</span></div>
      {csvRows.map((row, index) => <label className={`import-row ${row.errors.length ? 'duplicate' : ''}`} key={`${index}-${row.transaction.date}-${row.transaction.amount}`}>
        <input type="checkbox" checked={row.selected} disabled={!!row.errors.length} onChange={(e) => setCsvRows((old) => old.map((item, i) => i === index ? { ...item, selected: e.target.checked } : item))} />
        <span><b>{row.transaction.date} / {row.transaction.type} / {row.transaction.category}</b><small>{row.transaction.paymentMode} / {row.transaction.accountName || row.accountName || 'Unassigned'} / {row.transaction.taxTag || 'No tax tag'} / {row.transaction.note || '-'}</small>{row.errors.length > 0 && <em>{row.errors.join(', ')}</em>}{row.warnings.length > 0 && <i>{row.warnings.join(', ')}</i>}</span>
        <strong>{row.transaction.amount}</strong>
      </label>)}
      <button className="btn primary wide" disabled={csvSaving || !csvRows.some((row) => row.selected)} onClick={saveCsvRows}>{csvSaving ? 'Importing...' : 'Import selected'}</button>
    </Modal>
    <ConfirmModal
      open={!!pendingAction}
      title={pendingAction === 'daily' ? 'Submit transaction?' : pendingAction === 'bulk' ? 'Submit bulk entries?' : 'Submit plan?'}
      message={pendingAction === 'bulk' ? 'Please confirm before saving these entries.' : 'Please confirm the details before saving.'}
      details={pendingAction === 'daily' ? [
        { label: 'Type', value: tx.type || 'Expense' },
        { label: 'Category', value: tx.category || cats[0] },
        { label: 'Amount', value: tx.amount || 0 },
        { label: 'Payment mode', value: tx.paymentMode || paymentModes[0] },
        { label: 'Account', value: selectedAccount ? `${selectedAccount.name} (${selectedAccount.type.replace('_', ' ')})` : 'Unassigned' },
        { label: 'Date', value: tx.date || todayISO() }
      ] : pendingAction === 'plan' ? [
        { label: 'Plan', value: plan.title },
        { label: 'Amount', value: plan.amount },
        { label: 'Due date', value: plan.dueDate },
        { label: 'Payment mode', value: plan.paymentMode },
        { label: 'Paid from', value: plan.paidFromAccountName || 'Unassigned' },
        { label: 'Calendar', value: plan.calendarSyncEnabled ? `Prepared, ${plan.reminderDaysBefore} day(s) before` : 'Off' }
      ] : [
        { label: 'Lines', value: bulk.split(/\r?\n/).filter(Boolean).length },
        { label: 'Default type', value: tx.type || 'Expense' },
        { label: 'Default payment mode', value: tx.paymentMode || paymentModes[0] }
      ]}
      confirmLabel={pendingAction === 'bulk' ? 'Submit bulk' : 'Submit'}
      loading={saving}
      onCancel={() => setPendingAction(null)}
      onConfirm={confirm}
    />
  </div>;
}
